import type { SceneManager } from '../scene-manager.class';
import { RenderScheduler } from '../render-scheduler.class';

export type StartRenderLoopParams = {
	onFrame?: (delta: number) => void;
	shouldContinue?: () => boolean;
};

/**
 * Create a RenderScheduler and start the continuous render loop.
 *
 * The scheduler replaces the old unconditional rAF loop.  It supports both
 * single-frame renders (`requestRender()`) and a continuous loop that
 * self-terminates when `shouldContinue()` returns false.
 *
 * For now the loop is started immediately after creation to preserve the
 * existing behaviour.  Future tasks will wire up smart start/stop triggers
 * via camera-controls wake/sleep and @Publish events.
 */
export const startRenderLoopCfn = (self: SceneManager, params: StartRenderLoopParams): void => {
	const { onFrame, shouldContinue } = params;

	// If a scheduler already exists, just start its loop (idempotent)
	if (self.renderScheduler) {
		self.renderScheduler.startLoop();
		return;
	}

	// Create the scheduler
	const scheduler = new RenderScheduler({
		onFrame: (delta) => {
			if (onFrame) {
				onFrame(delta);
			}
		},
		render: () => {
			self.render();
		},
		getDelta: () => {
			return self.getDelta();
		},
		shouldContinue: shouldContinue ?? (() => true)
	});

	self.setRenderScheduler(scheduler);

	// Start the continuous loop
	scheduler.startLoop();
};
